import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader, money } from '@/components/PageHeader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Share2, Plus, Check, X, Eye, Trash2, Clock, Inbox, UserPlus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function NetworkPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('children');
  const [children, setChildren] = useState([]);
  const [sent, setSent] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ child_email: '', color: '#C9A84C', label: '' });
  const [overview, setOverview] = useState(null);

  const load = async () => {
    const [c, s, r] = await Promise.all([
      api.get('/network/children'), api.get('/network/sent'), api.get('/network/requests'),
    ]);
    setChildren(c.data); setSent(s.data); setRequests(r.data);
  };
  useEffect(() => { load(); }, []);

  const adopt = async () => {
    if (!form.child_email) return toast.error('Correo requerido');
    try { await api.post('/network/adopt', form); toast.success('Solicitud enviada. Espera la aprobaci\u00f3n del titular.'); setOpen(false); setForm({ child_email: '', color: '#C9A84C', label: '' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };
  const decide = async (id, decision) => { await api.post(`/network/requests/${id}/${decision}`); toast.success(decision === 'approve' ? 'Cuenta vinculada' : 'Solicitud rechazada'); load(); };
  const unlink = async (id) => { await api.delete(`/network/children/${id}`); load(); };
  const viewChild = async (childId) => { const { data } = await api.get(`/network/children/${childId}/overview`); setOverview(data); };

  return (
    <div data-testid="network-module">
      <PageHeader kicker="Red de cuentas" title="Cuentas Padre / Hija" subtitle="Adopta otras cuentas para ver sus movimientos (solo lectura) sin exponer los tuyos. Cada cuenta con su color."
        actions={<button data-testid="adopt-btn" onClick={() => setOpen(true)} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#C9A84C' }}><Plus size={16} /> Vincular cuenta</button>} />

      <div className="pp-glass p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="network-assistant-hint">
        <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(62,180,137,0.12)' }}><UserPlus size={18} className="pp-mint" /></span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold">¿Buscas conectar un ASISTENTE?</div>
          <div className="text-xs text-muted-foreground">Aquí gestionas cuentas Padre/Hija (solo lectura). Para invitar asistentes con permisos por módulo, ve a Asistentes.</div>
        </div>
        <button data-testid="goto-assistants" onClick={() => navigate('/plannerapp/assistants')} className="h-9 px-4 rounded-xl text-sm border flex items-center gap-1.5 pp-mint">Ir a Asistentes <ArrowRight size={14} /></button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="pp-glass">
          <TabsTrigger value="children" data-testid="tab-children">Mis cuentas ({children.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Solicitudes {requests.length > 0 && <span className="ml-1 px-1.5 rounded-full text-[10px] text-[#07090A]" style={{ background: '#C9A84C' }}>{requests.length}</span>}</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Enviadas ({sent.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'children' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((c) => (
            <div key={c.id} className="pp-glass p-5" data-testid={`child-${c.child_account_id}`} style={{ borderTop: `4px solid ${c.color}` }}>
              <div className="flex items-center gap-2 mb-3"><span className="w-3 h-3 rounded-full" style={{ background: c.color }} /><span className="font-semibold">{c.label}</span></div>
              <div className="text-xs text-muted-foreground mb-4">Vinculada · solo lectura</div>
              <div className="flex gap-2">
                <button data-testid={`view-child-${c.child_account_id}`} onClick={() => viewChild(c.child_account_id)} className="flex-1 h-9 rounded-lg text-xs border flex items-center justify-center gap-1 pp-mint"><Eye size={13} /> Ver panel</button>
                <button onClick={() => unlink(c.id)} className="h-9 px-3 rounded-lg text-xs border text-red-400"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {children.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground col-span-full">A\u00fan no tienes cuentas vinculadas. Un usuario Pro puede ser padre de infinitas cuentas.</div>}
        </div>
      )}

      {tab === 'requests' && (
        <div className="space-y-3 max-w-2xl">
          {requests.map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} className="pp-glass p-4 flex items-center gap-3" data-testid={`request-${r.id}`}>
              <span className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: 'rgba(201,168,76,0.15)' }}><Inbox size={16} className="pp-gold" /></span>
              <div className="flex-1"><div className="text-sm font-medium">{r.parent_name} quiere vincular tu cuenta</div><div className="text-[11px] text-muted-foreground">Solo podr\u00e1 ver (lectura), no editar.</div></div>
              <button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, 'approve')} className="h-9 px-3 rounded-lg text-xs text-[#07090A] flex items-center gap-1" style={{ background: '#3EB489' }}><Check size={13} /> Aprobar</button>
              <button onClick={() => decide(r.id, 'reject')} className="h-9 px-3 rounded-lg text-xs border text-red-400 flex items-center gap-1"><X size={13} /> Rechazar</button>
            </motion.div>
          ))}
          {requests.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground">Sin solicitudes entrantes.</div>}
        </div>
      )}

      {tab === 'sent' && (
        <div className="space-y-2 max-w-2xl">
          {sent.map((s) => (
            <div key={s.id} className="pp-glass p-4 flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              <span className="flex-1 text-sm">{s.child_name}</span>
              <span className={`text-xs ${s.status === 'active' ? 'pp-mint' : s.status === 'pending' ? 'pp-gold' : 'text-red-400'}`}>{s.status === 'active' ? 'Activa' : s.status === 'pending' ? 'Pendiente' : 'Rechazada'}</span>
            </div>
          ))}
          {sent.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground">No has enviado solicitudes.</div>}
        </div>
      )}

      {/* Adopt dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="pp-glass-strong"><DialogHeader><DialogTitle>Vincular una cuenta (como padre)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Ingresa el correo del titular de la cuenta hija. Recibir\u00e1 una solicitud para aprobar. T\u00fa ver\u00e1s sus movimientos; ellos NO ver\u00e1n los tuyos.</p>
            <input data-testid="adopt-email" value={form.child_email} onChange={(e) => setForm({ ...form, child_email: e.target.value })} placeholder="correo@empresa.com" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#C9A84C]" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Etiqueta (ej. Compa\u00f1\u00eda)" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none" />
            <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Color distintivo:</span><input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-16 rounded-xl bg-transparent border" /></div>
            <button data-testid="adopt-submit" onClick={adopt} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#C9A84C' }}>Enviar solicitud</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Child overview */}
      <Dialog open={!!overview} onOpenChange={(o) => !o && setOverview(null)}>
        <DialogContent className="pp-glass-strong max-w-lg"><DialogHeader><DialogTitle style={{ color: overview?.color }}>Panel de {overview?.child_name}</DialogTitle></DialogHeader>
          {overview && <div className="space-y-4 max-h-[60vh] overflow-auto pp-scroll">
            <div className="grid grid-cols-3 gap-3">
              <div className="pp-glass p-3 text-center"><div className="text-xl font-black">{overview.tasks_pending}</div><div className="text-[10px] text-muted-foreground">Tareas pend.</div></div>
              <div className="pp-glass p-3 text-center"><div className="text-xl font-black">{overview.events_total}</div><div className="text-[10px] text-muted-foreground">Eventos</div></div>
              <div className="pp-glass p-3 text-center"><div className="text-xl font-black">{money(overview.net)}</div><div className="text-[10px] text-muted-foreground">Neto</div></div>
            </div>
            <div><div className="text-sm font-semibold mb-2">Pr\u00f3ximos eventos</div>{overview.upcoming.map((e) => <div key={e.id} className="flex items-center gap-2 text-sm py-1"><Clock size={12} className="pp-mint" /><span className="flex-1 truncate">{e.title}</span><span className="text-[11px] text-muted-foreground">{new Date(e.start).toLocaleDateString('es')}</span></div>)}</div>
            <div className="text-[11px] text-muted-foreground">Vista de solo lectura. Tus datos permanecen privados para esta cuenta.</div>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
